import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import EntityName from '../../../core/enums/EntityName';
import { generateEntityFetchAsyncAction, generateEntityFetchListAsyncAction } from '../../../core/state/entity/generateEntityAsyncActions';
import { Actions } from '../../../core/state/bookings/actions';
import ProjectDetails from '../project-details/ProjectDetails';
import Button from '../../../components/form/button/Button';
import Icon from '../../../components/icon/Icon';
import DateSelector from '../calendar/date-selector/DateSelector';
import { useBookingLayout } from '../hooks';
import { BookingLayout } from '../types';
import BookingList from '../list/BookingList';
import Calendar from '../calendar/Calendar';
import { BookingModalType } from '../../../core/state/bookings/types';
import { useInternalUser } from '../../../core/hooks/useInternalUser';
import ManageBookingInternalModal from '../modals/create-internal/ManageBookingInternalModal';
import { getStateStatus } from '../../../core/state/bookings/selectors';
import { StateStatus } from '../../../core/enums/StateStatus';
import SendEmailAfterBookingModal from '../modals/create-internal/send-email/SendEmailAfterBookingModal';
import CreateBookingExternalModal from '../modals/create-external/CreateBookingExternalModal';
import LogisticPlanModal from '../modals/logistic-plan/LogisticPlanModal';

import './BookingsMobileScreen.scss';

const BookingsMobileScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const layout = useBookingLayout();
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);
  const isInternal = useInternalUser();
  const stateStatus = useSelector(getStateStatus);
  const [isSendEmailModal, setIsSendEmailModal] = React.useState(false);
  const [bookingId, setBookingId] = React.useState('');

  const fetchProjectSettings = useMemo(() => generateEntityFetchAsyncAction(EntityName.BookingProjectSettings), []);
  const fetchBlockedSlots = useMemo(() => generateEntityFetchListAsyncAction(EntityName.BlockedSlot), []);

  React.useEffect(() => {
    if (projectId) {
      dispatch(Actions.changeDate(new Date()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(Actions.closeModal());
    dispatch(Actions.changeSelectedProject(projectId));
    dispatch(Actions.resetFilters());

    dispatch(fetchProjectSettings(projectId));
    dispatch(fetchBlockedSlots({ projectId }));
  }, [dispatch, fetchBlockedSlots, fetchProjectSettings, projectId]);

  const handleOpenSendEmailModal = (id: string) => {
    setBookingId(id);
    setIsSendEmailModal(true);
  };

  return (
    <div className="bookings-mobile-screen">
      <div className="bookings-mobile-screen__wrapper">
        <Button
          className="bookings-mobile-screen__create-btn"
          disabled={stateStatus === StateStatus.Busy}
          onClick={() => {
            dispatch(
              Actions.openModal({
                type: isInternal ? BookingModalType.Internal : BookingModalType.LogisticPlan,
                initialValues: undefined
              })
            );
          }}
          buttonTypes={['blue', 'block']}
        >
          <Icon className="bookings-mobile-screen__create-icon" icon="plus" svg />
          {t('common.createNew')}
        </Button>
        <div className="bookings-mobile-screen__inner">
          <ProjectDetails projectId={projectId} />
          <DateSelector />
          <div className="bookings-mobile-screen__navigation">
            <div
              className={`bookings-mobile-screen__navigation-tab ${
                layout === BookingLayout.List && 'bookings-mobile-screen__navigation-tab--active'
              }`}
            >
              <Link to={`/projects/${projectId}/bookings/list`}>{t('booking.tab.listView')}</Link>
            </div>
            <div
              className={`bookings-mobile-screen__navigation-tab ${
                layout === BookingLayout.Calendar && 'bookings-mobile-screen__navigation-tab--active'
              }`}
            >
              <Link to={`/projects/${projectId}/bookings/calendar`}>{t('booking.tab.calendarView')}</Link>
            </div>
          </div>
          {layout === BookingLayout.Calendar && <Calendar />}
          {layout === BookingLayout.List && <BookingList />}
        </div>
      </div>

      {isInternal && <ManageBookingInternalModal openSendEmailModal={(id) => handleOpenSendEmailModal(id)} />}
      {!isInternal && <CreateBookingExternalModal />}
      <LogisticPlanModal />
      <SendEmailAfterBookingModal isOpen={isSendEmailModal} closeModal={() => setIsSendEmailModal(false)} bookingId={bookingId} />
    </div>
  );
};

export default BookingsMobileScreen;
